import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import { AIEnrichedTextArea } from '@/shared/components/ui/AIEnhancedTextArea';
import { WizardStepProps } from '../types/wizard.types';
import { ComponentReturnTypeMap, InteractionType } from '@momentum/types';
import { useEnrichFeedback } from '@/shared/hooks';

interface WizardTextAreaStepProps
  extends WizardStepProps<ComponentReturnTypeMap['TextArea']> {
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  interactionType?: InteractionType.PRAISE | InteractionType.FEEDBACK;
}

export const WizardTextAreaStep: React.FC<WizardTextAreaStepProps> = ({
  value,
  onChange,
  onNext,
  onPrevious: _onPrevious,
  error,
  disabled,
  placeholder = '',
  maxLength = 500,
  minLength = 0,
  required = false,
  interactionType = InteractionType.FEEDBACK,
  setSubmitHandler,
}) => {
  const { t } = useTranslation('wizard');
  // Local state to track the current input value without triggering navigation
  const [localValue, setLocalValue] = useState(value || '');

  // Update local state when external value changes (e.g., when navigating back)
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleNext = () => {
    // Update the wizard state with the current value and proceed to next step
    onChange(localValue);
    if (onNext) {
      onNext(localValue);
    }
  };

  // Register submit handler for the card footer's Next button
  const typedSetSubmitHandler = setSubmitHandler as
    | ((handler: (() => void) | null) => void)
    | undefined;
  typedSetSubmitHandler?.(handleNext);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      handleNext();
    }
  };

  const enrichFeedbackMutation = useEnrichFeedback();

  return (
    <div className="w-full mx-auto space-y-6">
      <div className="space-y-4">
        <AIEnrichedTextArea
          value={localValue}
          onChange={setLocalValue}
          mutation={enrichFeedbackMutation}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          maxLength={maxLength}
          minLength={minLength}
          rows={4}
          interactionType={interactionType}
          className="resize-none bg-white border-sm shadow-sm min-h-28"
        />
      </div>

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {error}
        </div>
      )}

      {!typedSetSubmitHandler && (
        <div className="flex justify-end">
          <Button
            onClick={handleNext}
            disabled={disabled || (required && !localValue.trim())}
          >
            {t('common.buttons.next')}
          </Button>
        </div>
      )}
    </div>
  );
};

/* 
MongoDB Documents that should use this component:
- All questions with type: "text-area" (28 documents)
- Examples: 
  - "Describe recipientPlaceholder's response / service"
  - "In which framework were they written?"
*/
